<template>
  <div
    v-if="open"
    class="dlg-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="ticket-create-title"
    @keydown.esc="emitClose"
  >
    <div class="dlg">
      <div class="dlg-header">
        <h2
          id="ticket-create-title"
          class="my-1"
        >
          Neues Ticket
        </h2>
        <button
          class="icon-btn"
          aria-label="Schließen"
          @click="emitClose"
        >
          ✕
        </button>
      </div>

      <form
        class="form"
        @submit.prevent="submit"
      >
        <div class="field mb-2">
          <label for="tc-subject">Betreff</label>
          <input
            id="tc-subject"
            v-model.trim="subject"
            required
            autocomplete="off"
          >
          <p class="help">
            Kurz und prägnant formulieren.
          </p>
        </div>

        <div class="field mb-2">
          <label for="tc-message">Nachricht</label>
          <textarea
            id="tc-message"
            v-model.trim="message"
            rows="4"
          />
        </div>

        <div class="field checkbox-row">
          <input
            id="tc-private"
            v-model="isPrivate"
            type="checkbox"
          >
          <label
            for="tc-private"
            class="checkbox-label"
          >Privates Ticket</label>
        </div>

        <div class="field">
          <label for="tc-attachments">Anhänge</label>
          <input
            id="tc-attachments"
            ref="fileInput"
            type="file"
            multiple
            @change="onFiles"
          >
          <p
            v-if="files.length"
            class="help"
          >
            {{ files.length }} Datei(en) ausgewählt
          </p>
        </div>

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
        <p
          v-if="success"
          class="notice notice-success mt-12"
          role="status"
        >
          Ticket erstellt.
        </p>

        <div class="dlg-footer">
          <button
            type="button"
            class="btn"
            :disabled="loading"
            @click="emitClose"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner"
              aria-hidden="true"
            />
            Erstellen
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useTicketsStore } from '@/stores/tickets';

const props = defineProps({
  open: { type: Boolean, default: false }
});
const emit = defineEmits(['close', 'created']);

const store = useTicketsStore();

const subject = ref('');
const message = ref('');
const isPrivate = ref(false);
const files = ref([]);
const fileInput = ref(null);
const loading = ref(false);
const error = ref('');
const success = ref(false);

watch(() => props.open, (v) => {
  if (v) reset();
});

function reset() {
  subject.value = '';
  message.value = '';
  isPrivate.value = false;
  files.value = [];
  error.value = '';
  success.value = false;
  if (fileInput.value) fileInput.value.value = '';
}

function emitClose() {
  emit('close');
}

function onFiles(e) {
  files.value = Array.from(e.target.files || []);
}

async function submit() {
  error.value = '';
  success.value = false;
  loading.value = true;
  try {
    const res = await store.create({
      subject: subject.value,
      message: message.value,
      attachments: files.value,
      isPrivate: isPrivate.value
    });
    success.value = true;
    emit('created', res);
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Erstellen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.dlg-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  display:flex; align-items:center; justify-content:center; z-index: 1000;
}
.dlg {
  width: min(640px, 94vw);
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,.18);
  padding: 16px;
}
.dlg-header {
  display:flex; align-items:center; justify-content:space-between;
  border-bottom: 1px solid var(--border); padding-bottom: 8px; margin-bottom: 12px;
}
.dlg-footer {
  display:flex; gap:8px; justify-content:flex-end; margin-top: 8px;
}
.form { display:flex; flex-direction:column; gap:12px; }
.field input, .field textarea {
  height: 38px; padding: 8px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface);
}
.field textarea { height: auto; }

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

.checkbox-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.checkbox-label {
  margin: 0;
}

.icon-btn {
  display:inline-flex; align-items:center; justify-content:center; height:36px; width:36px;
  border-radius:8px; border:1px solid var(--border); background:var(--surface); color:var(--text); cursor:pointer;
}
.icon-btn:hover { background: color-mix(in srgb, var(--primary) 8%, transparent); }
</style>
